import React, { useEffect, useState } from 'react'
import { InputField } from '../../../Shared/Radix/Inputs/InputField';
import { useConfigureServers } from './hooks';
import { IConfigureServer } from '../../../../store/api/admin/types';

interface ConfigureServersProps {
  onValidationChange: (isValid: boolean) => void;
  primary_address:            string;
  secondary_address:          string;
  observer_address:           string;
  heartbeat_freqency:         number;
  missed_heartbeat_threshold: number;
  secondary_username:         string;
  secondary_password:         string;
}

const isValidIPv4 = (ip: string): boolean => {
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipv4Regex.test(ip)) return false;
  
  const parts = ip.split('.');
  return parts.every(part => {
    const num = parseInt(part, 10);
    return num >= 0 && num <= 255;
  });
};

const isValidIPv6 = (ip: string): boolean => {
  const ipv6Regex = /^(?:(?:[a-fA-F\d]{1,4}:){7}[a-fA-F\d]{1,4}|(?:[a-fA-F\d]{1,4}:){1,7}:|(?:[a-fA-F\d]{1,4}:){1,6}:[a-fA-F\d]{1,4}|(?:[a-fA-F\d]{1,4}:){1,5}(?::[a-fA-F\d]{1,4}){1,2}|(?:[a-fA-F\d]{1,4}:){1,4}(?::[a-fA-F\d]{1,4}){1,3}|(?:[a-fA-F\d]{1,4}:){1,3}(?::[a-fA-F\d]{1,4}){1,4}|(?:[a-fA-F\d]{1,4}:){1,2}(?::[a-fA-F\d]{1,4}){1,5}|[a-fA-F\d]{1,4}:(?:(?::[a-fA-F\d]{1,4}){1,6})|:(?:(?::[a-fA-F\d]{1,4}){1,7}|:)|fe80:(?::[a-fA-F\d]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[a-fA-F\d]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
  return ipv6Regex.test(ip);
};

const isValidDomain = (domain: string): boolean => {
  const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
};

const validateObserverHost = (host: string): string => {
  if (!host) return 'Observer host is required';
  if (!isValidIPv4(host) && !isValidIPv6(host) && !isValidDomain(host)) {
    return 'Please enter a valid IPv4, IPv6 address, or domain name';
  }
  return '';
};

const validateIP = (ip: string): string => {
  if (!ip) return 'IP address is required';
  if (!isValidIPv4(ip) && !isValidIPv6(ip)) return 'Please enter a valid IPv4 or IPv6 address';
  return '';
};

const ConfigureServers: React.FC<ConfigureServersProps> = ({ onValidationChange }) => {
  const { handleOnChange, handleSelectChange, serverConfig, handleBlur, error, touched } = useConfigureServers();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onFieldChange = (name: string, value: string) => {
    handleOnChange({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>);
  };

  const onFieldBlur = (fieldName: string, validateFn: (val: string) => string) => {
    return () => handleBlur(fieldName, validateFn(serverConfig[fieldName]));
  };

  useEffect(() => {
    const isValid = 
      serverConfig.primary_address?.trim() !== '' &&
      serverConfig.secondary_address?.trim() !== '' &&
      serverConfig.observer_address?.trim() !== '' &&
      //serverConfig.secondary_username?.trim() !== '' &&
      //serverConfig.secondary_password?.trim() !== '' &&
      !validateIP(serverConfig.primary_address) &&
      !validateIP(serverConfig.secondary_address) &&
      !validateObserverHost(serverConfig.observer_address);

    onValidationChange(isValid);
  }, [
    serverConfig.primary_address,
    serverConfig.secondary_address,
    serverConfig.observer_address,
    //serverConfig.secondary_username,
    //serverConfig.secondary_password,
    onValidationChange
  ]);

  return (
    <div className="space-y-5">
      <div>
        <InputField
          label="My IP"
          name="primary_address"
          value={serverConfig.primary_address}
          onChange={onFieldChange}
          onBlur={onFieldBlur('primary_address', validateIP)}
          required
        />
        {touched.primary_address && serverConfig.primary_address?.trim() !== '' && validateIP(serverConfig.primary_address) && (
          <p className="text-xs text-destructive mt-1">{validateIP(serverConfig.primary_address)}</p>
        )}
      </div>

      <div>
        <InputField
          label="Standby IP"
          name="secondary_address"
          value={serverConfig.secondary_address}
          onChange={onFieldChange}
          onBlur={onFieldBlur('secondary_address', validateIP)}
          required
        />
        {touched.secondary_address && serverConfig.secondary_address?.trim() !== '' && validateIP(serverConfig.secondary_address) && (
          <p className="text-xs text-destructive mt-1">{validateIP(serverConfig.secondary_address)}</p>
        )}
      </div>

      <div>
        <InputField
          label="Observer Host"
          name="observer_address"
          value={serverConfig.observer_address}
          onChange={onFieldChange}
          onBlur={onFieldBlur('observer_address', validateObserverHost)}
          required
        />
        {touched.observer_address && serverConfig.observer_address?.trim() !== '' && validateObserverHost(serverConfig.observer_address) && (
          <p className="text-xs text-destructive mt-1">{validateObserverHost(serverConfig.observer_address)}</p>
        )}
      </div>
    </div>
  )
}

export default ConfigureServers